// src/pages/Admin/Views/SellerManagement/SellerManagement.jsx
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Import shadcn/ui and icon components
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../../../components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select"; // --- NEW IMPORTS ---

// Import services and components
import { api } from '../../../../services/apiServices';
import withAdminActionLogger from '../../../../hocs/withAdminActionLogger';
import SellerInfoSection from '../../../../components/Admin/SellerManagement/SellerInfoSection';
import SellerCommsSection from '../../../../components/Admin/SellerManagement/SellerCommsSection';
import SellerCommsHistorySection from '../../../../components/Admin/SellerManagement/SellerCommsHistorySection';
import CoinResetAssuranceSection from '../../../../components/Admin/SellerManagement/CoinResetAssuranceSection';
import EditCoinsModal from '../../../../components/Admin/SellerManagement/EditCoinsModal';
import ViewContentIssuesModal from '../../../../components/Admin/SellerManagement/ViewContentIssuesModal';
import SellerFinancials from '../../../../components/Admin/SellerManagement/PaymentsTab/SellerFinancials';
import ActivateCouponSection from './ActivateCouponSection';
import GrantFreeResponses from '../../../../components/Admin/SellerManagement/PaymentsTab/GrantFreeResponses';
import ActiveCouponsSection from '../../../../components/Admin/SellerManagement/PaymentsTab/ActiveCouponsSection';
import LeadHistoryTab from '../../../../components/Admin/SellerManagement/LeadHistory/LeadHistoryTab';
import ReviewModerationTab from '../../../../components/Admin/SellerManagement/Reviews/ReviewModerationTab';
import AdminSpecialSetCreator from '../../../../components/Admin/SellerManagement/SalesManagement/AdminSpecialSetCreator';
import SellerManagementStatusHistory from '../../../../components/Admin/SellerManagement/SalesManagement/SellerManagementStatusHistory';
import AdminNotesSection from '../../../../components/Admin/SellerManagement/Notes/AdminNotesSection';
import SellerBillingInfo from '../../../../components/Admin/SellerManagement/PaymentsTab/SellerBillingInfo';

import { Badge } from '../../../../components/ui/badge';
import { Separator } from '../../../../components/ui/separator';
import { usePermissions } from '../../../../hooks/usePermissions';
import AdminSetPriceCalculator from '../../../../components/Admin/SellerManagement/SalesManagement/AdminSetPriceCalculator';

const SellerManagementPage = ({ logAction }) => {
    const { sellerProfileId, tab } = useParams();
    const navigate = useNavigate();

    const [sellerInfo, setSellerInfo] = useState(null);
    const [commsInfo, setCommsInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const { hasPermission } = usePermissions();
    
    const activeTab = tab || 'info';

    const [isEditCoinsModalOpen, setIsEditCoinsModalOpen] = useState(false);
    const [isIssuesModalOpen, setIsIssuesModalOpen] = useState(false);
    const activeCouponsRef = useRef(null);

    const getTruncatedDisplayName = (info) => {
        if (!info) return "Unknown Seller";
        const rawName = info.company_name || info.name || "Unknown Seller";
        return rawName.length > 50 ? `${rawName.substring(0, 50)}...` : rawName;
    };

    const fetchInfoAndCommsData = useCallback(async () => {
        try {
            const [infoRes, commsRes] = await Promise.all([
                api.get(`/admin/sellers/${sellerProfileId}/info`),
                api.get(`/admin/sellers/${sellerProfileId}/comms-info`)
            ]);
            setSellerInfo(infoRes.data);
            setCommsInfo(commsRes.data);
        } catch (err) {
            setError('Failed to fetch seller data.');
        } finally {
            setIsLoading(false);
        }
    }, [sellerProfileId]);

    useEffect(() => {
        setIsLoading(true);
        fetchInfoAndCommsData();
    }, [fetchInfoAndCommsData]);

    useEffect(() => {
        if (sellerInfo) {
            const displayName = getTruncatedDisplayName(sellerInfo);
            document.title = `S | ${displayName} (${sellerProfileId}) - Seller Management`;
        } else {
            document.title = "Seller Management";
        }
    }, [sellerInfo, sellerProfileId, activeTab]);

    const handleTabChange = (newTab) => {
        navigate(`/admin/sellers/${sellerProfileId}/view/${newTab}`, { replace: true });
    };

    const handleUpdateSuccess = (updatedData, logDetails) => {
        setSellerInfo(updatedData);
        // Logging is handled on the backend for this action
    };

    const handleFreeResponseUpdateSuccess = (updatedData) => {
        setSellerInfo(updatedData);
        // Logging is handled on the backend for this action
    };

    if (isLoading && !sellerInfo) return <div className="p-4 md:p-6 text-muted-foreground">Loading seller information...</div>;
    if (error) return <div className="p-4 md:p-6 text-destructive font-medium">{error}</div>;

    const sellerDisplayName = getTruncatedDisplayName(sellerInfo);

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto w-full">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-6">
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground m-0">{sellerDisplayName}</h2>
                {!sellerInfo.is_paid_seller && (
                    <Badge variant={'outline'} className="text-xs bg-green-500 text-white border-transparent w-fit">
                        WSE
                    </Badge>
                )}
                {sellerInfo.assigned_admin_email && !sellerInfo.is_default_owner && hasPermission('sales:manages-seller-assignment') && (
                    <Badge variant={'outline'} className="text-xs w-fit">
                        {sellerInfo.assigned_admin_email}
                    </Badge>
                )}
                {sellerInfo.is_managed_seller && hasPermission('sales:manages-seller-accounts') && (
                    <Badge variant={'outline'} className="text-xs bg-blue-600 text-white border-transparent w-fit">
                        Key Account
                    </Badge>
                )}
            </div>

            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                
                {/* --- MOBILE NAVIGATION (Dropdown) --- */}
                <div className="md:hidden mb-4">
                    <Select value={activeTab} onValueChange={handleTabChange}>
                        <SelectTrigger className="w-full bg-slate-50 border-slate-200 h-12 font-medium">
                            <SelectValue placeholder="Select a section" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="info">Seller Info & Comms</SelectItem>
                            <SelectItem value="payments">Payments</SelectItem>
                            <SelectItem value="history">Lead History</SelectItem>
                            {hasPermission("cx:manage-reviews") && (
                                <SelectItem value="reviews">Reviews</SelectItem>
                            )}
                            <SelectItem value="management">Seller Management</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* --- DESKTOP NAVIGATION (Tabs) --- */}
                <TabsList className="hidden md:flex w-full justify-evenly h-auto p-1 bg-slate-200 rounded-lg mb-6 flex-wrap"> 
                    <TabsTrigger value="info" className="px-4 py-2">Seller Info & Comms</TabsTrigger>
                    <TabsTrigger value="payments" className="px-4 py-2">Payments</TabsTrigger>
                    <TabsTrigger value="history" className="px-4 py-2">Lead History</TabsTrigger>
                    {hasPermission("cx:manage-reviews") && (
                        <TabsTrigger value="reviews" className="px-4 py-2">Reviews</TabsTrigger>
                    )}
                    <TabsTrigger value="management" className="px-4 py-2">Seller Management</TabsTrigger>                    
                </TabsList>

                <TabsContent value="info" className="focus-visible:outline-none focus-visible:ring-0">
                    <div className="space-y-6">
                        <SellerInfoSection 
                            sellerData={sellerInfo} 
                            onEditCoins={() => setIsEditCoinsModalOpen(true)} 
                            onViewContentIssues={() => setIsIssuesModalOpen(true)} 
                            sellerProfileId={sellerProfileId}/>
                        <SellerCommsSection commsData={commsInfo} />
                        <SellerCommsHistorySection sellerProfileId={sellerProfileId} />
                        <CoinResetAssuranceSection
                            sellerProfileId={sellerProfileId}
                            onGranted={fetchInfoAndCommsData}
                        />
                    </div>
                    <div className="mt-6">
                        <AdminNotesSection 
                                contextType="SELLER"
                                contextId={sellerProfileId}
                                contextTab="INFO"
                        />
                    </div>
                </TabsContent>

                <TabsContent value="payments" className="focus-visible:outline-none focus-visible:ring-0">
                    <Accordion type="single" collapsible className="w-full bg-card rounded-lg border shadow-sm" defaultValue="item-billing">
                        <AccordionItem value="item-billing" className="border-b px-2 md:px-4">
                            <AccordionTrigger className="hover:no-underline py-4 font-semibold">Billing & GST Details</AccordionTrigger>
                            <AccordionContent className="pb-4">
                                <div className="p-1">
                                    <SellerBillingInfo sellerProfileId={sellerProfileId} />
                                </div>
                            </AccordionContent>
                        </AccordionItem>
             
                        {hasPermission("invoices:view") && (
                            <AccordionItem value="item-4" className="border-b-0 px-2 md:px-4">
                                <AccordionTrigger className="hover:no-underline py-4 font-semibold">Invoices & Credit Notes</AccordionTrigger>
                                <AccordionContent className="pb-4">
                                    <SellerFinancials sellerProfileId={sellerProfileId} />
                                </AccordionContent>
                            </AccordionItem>
                        )}
                    </Accordion>
                    
                    <div className="mt-6">
                        <AdminNotesSection 
                            contextType="SELLER"
                            contextId={sellerProfileId}
                            contextTab="PAYMENTS"
                        />
                    </div>
                </TabsContent>

                <TabsContent value="history" className="focus-visible:outline-none focus-visible:ring-0">                    
                    <div className="mb-6">
                        <AdminNotesSection 
                            contextType="SELLER"
                            contextId={sellerProfileId}
                            contextTab="LEADS"
                        />
                    </div>
                    <LeadHistoryTab sellerProfileId={sellerProfileId} />
                </TabsContent>
                
                {hasPermission("cx:manage-reviews") && (
                    <TabsContent value="reviews" className="focus-visible:outline-none focus-visible:ring-0">
                        <div className="mb-6">
                            <AdminNotesSection 
                                contextType="SELLER"
                                contextId={sellerProfileId}
                                contextTab="REVIEWS"
                            />
                        </div>
                        <ReviewModerationTab />
                    </TabsContent>
                )}
                
                <TabsContent value="management" className="space-y-6 focus-visible:outline-none focus-visible:ring-0">
                    <div className="mt-6">
                        <AdminNotesSection 
                            contextType="SELLER"
                            contextId={sellerProfileId}
                            contextTab="SALES"
                        />
                    </div>
                    <Accordion type="single" collapsible className="w-full bg-card rounded-lg border shadow-sm">
                        {hasPermission("sales:give-free-response") && (
                            <AccordionItem value="item-1" className="border-b px-2 md:px-4">
                                <AccordionTrigger className="hover:no-underline py-4 font-semibold">Grant Free Responses</AccordionTrigger>
                                <AccordionContent className="pb-4">
                                    <GrantFreeResponses 
                                        sellerProfileId={sellerProfileId} 
                                        onUpdateSuccess={handleFreeResponseUpdateSuccess}
                                    />
                                </AccordionContent>
                            </AccordionItem>
                        )}
                        <AccordionItem value="item-2" className="border-b-0 px-2 md:px-4">
                            <AccordionTrigger className="hover:no-underline py-4 font-semibold">Coupons</AccordionTrigger>
                            <AccordionContent className="pb-4 space-y-4">
                                <ActiveCouponsSection ref={activeCouponsRef} sellerProfileId={sellerProfileId} />
                                {hasPermission("coupons:activate") && (
                                    <>
                                        <Separator />
                                        <ActivateCouponSection
                                            sellerProfileId={sellerProfileId}
                                            onSuccess={() => activeCouponsRef.current?.refetch()}
                                        />
                                    </>
                                )}
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>

                    <SellerManagementStatusHistory
                        sellerProfileId={sellerProfileId}
                        isManagedSeller={sellerInfo.is_managed_seller}
                        onStatusChange={handleUpdateSuccess}
                    />

                    <div className="gap-6">
                        <div className="w-full">
                            <AdminSetPriceCalculator sellerProfileId={sellerProfileId} />
                        </div>
                        <div className="w-full">
                            <AdminSpecialSetCreator sellerProfileId={sellerProfileId} />
                        </div>
                    </div>
                </TabsContent>
            </Tabs>

            {isEditCoinsModalOpen && (
                <EditCoinsModal 
                    isOpen={isEditCoinsModalOpen} 
                    sellerId={sellerProfileId} 
                    currentBalance={sellerInfo?.coin_balance} 
                    onClose={() => setIsEditCoinsModalOpen(false)} 
                    onUpdateSuccess={handleUpdateSuccess} 
                />
            )}
            
            {isIssuesModalOpen && (
                <ViewContentIssuesModal 
                    issues={sellerInfo?.public_profile_content_issues} 
                    onClose={() => setIsIssuesModalOpen(false)} 
                />
            )}
        </div>
    );
};

export default withAdminActionLogger(SellerManagementPage);
